/**
 * Gun traverse: what the `.DAT` gun block's `+8`…`+14` BAM16 fields actually
 * are, and what a mount can and cannot be pointed at.
 *
 * ## They are arcs, not a dispersion cone
 *
 * The port read those four fields as a *field of fire* half-angle and used them
 * to size a random cone every round was scattered inside. `RB.EXE`'s own
 * `mg.c`/`shooting.c` say otherwise: a round is spawned with the shooter's
 * attitude copied verbatim and flies dead straight, and the four fields are the
 * **traverse clamp** on where the mount may be aimed — `+8/+10` an elevation
 * arc, `+12/+14` an azimuth arc, both signed BAM16 with a full turn at 65536.
 * That reading is what the fleet table has always looked like:
 *
 * - every **fixed** forward gun is `−500…+500` on both axes (`±2.747°`), the
 *   Albatros D.Va `−910…+910` (`±4.9988°`) — a tolerance band around the
 *   boresight, not a spray pattern;
 * - every **gunner's** station carries an arc that excludes zero: an elevation
 *   band well above or well below the fuselage line (`+4000…+10000` on a B.E.2's
 *   observer gun, `−8000…−2500` on a Gotha's ventral tunnel gun) and an azimuth
 *   arc that **wraps through the rear** (`+25000 → −25000`, i.e. 137.3° round
 *   past 180° to −137.3°). A cone reading cannot express either of those; a
 *   traverse reading is the only thing they can be.
 *
 * The two shapes therefore do different work. On a fixed gun the clamp is a
 * limit on how far the round's own spawn offsets (`gunnery.ts`'s angle-of-attack
 * pitch term and the damaged-airframe jitter) may carry it off the boresight,
 * and it binds only at extreme alpha. On a gunner's station it is the physical
 * limit of the mount: the observer traverses onto the target inside the arc, and
 * simply cannot be brought to bear outside it. That second half is what makes an
 * escort behave — a two-seater's gunner defends the tail and nothing else.
 *
 * Everything here is pure angle arithmetic in the shooter's **body** frame
 * (right +X, up +Y, forward −Z), so azimuth is `atan2(x, −z)` positive right and
 * elevation is `asin(y)` positive up — the same pair `geometry.ts`'s
 * `relativeGeometry` reports.
 */

import type { GunMountSpec, GunSpec } from "../flight/guns.ts";
import { normalizeVec3, type Vec3 } from "../flight/math3d.ts";

/** A direction in the body frame, split the way a mount is aimed. */
export interface Traverse {
  /** Positive to the right, in (−π, π]. */
  readonly azimuthRad: number;
  /** Positive up, in [−π/2, π/2]. */
  readonly elevationRad: number;
}

const BODY_FORWARD_TRAVERSE: Traverse = { azimuthRad: 0, elevationRad: 0 };

/** Splits a body-frame direction into the azimuth/elevation pair an arc is expressed in. */
export function traverseOf(direction: Vec3): Traverse {
  const unit = normalizeVec3(direction, { x: 0, y: 0, z: -1 });
  return { azimuthRad: Math.atan2(unit.x, -unit.z), elevationRad: Math.asin(Math.min(1, Math.max(-1, unit.y))) };
}

/** The inverse: the body-frame unit vector a mount aimed at `traverse` points along. */
export function traverseDirection(traverse: Traverse): Vec3 {
  const cosEl = Math.cos(traverse.elevationRad);
  return {
    x: cosEl * Math.sin(traverse.azimuthRad),
    y: Math.sin(traverse.elevationRad),
    z: -cosEl * Math.cos(traverse.azimuthRad),
  };
}

/** Signed difference `a − b` folded into (−π, π]. */
function wrapAngle(radians: number): number {
  const turn = 2 * Math.PI;
  const wrapped = ((radians + Math.PI) % turn + turn) % turn;
  return wrapped - Math.PI;
}

/** How far apart two angles are, in [0, π]. */
function angularGap(a: number, b: number): number {
  return Math.abs(wrapAngle(a - b));
}

/**
 * Whether `angle` lies inside the arc running from `from` to `to`. When
 * `from > to` the arc is the one that **wraps through ±π** — a rear-hemisphere
 * gunner's arc — rather than the empty interval a naive comparison would give.
 */
export function withinArc(angle: number, from: number, to: number): boolean {
  if (from <= to) return angle >= from && angle <= to;
  return angle >= from || angle <= to;
}

/** `angle` clamped into that arc, moving it to the nearer edge when it is outside. */
export function clampToArc(angle: number, from: number, to: number): number {
  if (withinArc(angle, from, to)) return angle;
  return angularGap(angle, from) <= angularGap(angle, to) ? from : to;
}

export interface MountAim {
  /** Where the mount ends up pointing, in the body frame. */
  readonly traverse: Traverse;
  /** True when the requested direction was inside the arc, so nothing had to be clamped. */
  readonly withinArc: boolean;
}

/** The requested aim, clamped into one mount's own elevation and azimuth arcs. */
export function aimMount(mount: GunMountSpec, requested: Traverse): MountAim {
  const inside =
    withinArc(requested.elevationRad, mount.elevationFromRad, mount.elevationToRad) &&
    withinArc(requested.azimuthRad, mount.azimuthFromRad, mount.azimuthToRad);
  if (inside) return { traverse: requested, withinArc: true };
  return {
    traverse: {
      azimuthRad: clampToArc(requested.azimuthRad, mount.azimuthFromRad, mount.azimuthToRad),
      elevationRad: clampToArc(requested.elevationRad, mount.elevationFromRad, mount.elevationToRad),
    },
    withinArc: false,
  };
}

export interface GunBearing {
  readonly mount: GunMountSpec;
  readonly traverse: Traverse;
  /** True when this station can actually be brought onto the target. */
  readonly bears: boolean;
}

/**
 * The gunner's station that can be brought onto a target lying in body-frame
 * direction `aim`, or `null` when none can. Only *flexible* stations are
 * considered: a fixed gun's arc is a clamp on the round it fires, not a
 * statement about where it can be pointed.
 */
export function flexibleBearing(gun: GunSpec, aim: Vec3): GunBearing | null {
  const requested = traverseOf(aim);
  for (const mount of gun.mounts) {
    if (!mount.flexible) continue;
    const aimed = aimMount(mount, requested);
    if (aimed.withinArc) return { mount, traverse: aimed.traverse, bears: true };
  }
  return null;
}

/**
 * Which station engages a target lying in body-frame direction `aim`, and where
 * that station ends up pointing.
 *
 * **A gunner who can reach the target wins over a fixed gun that is not
 * pointing at it.** That ordering is the whole behaviour of a two-seater: an
 * R.E.8 has both a pilot's Vickers and an observer's Lewis, and which one fires
 * is decided by where the enemy is, not by which is nominally the main gun. A
 * fixed gun is the fallback and always bears, because it is aimed by flying the
 * aeroplane and "close enough to shoot" is the pilot's judgement (the AI's
 * firing cone) rather than the `.DAT` arc.
 *
 * With nothing bearing at all — the five gunner-armed types, with the target
 * somewhere their observer cannot reach — `bears` is false, and the caller
 * holds its fire.
 */
export function bearingFor(gun: GunSpec, aim: Vec3): GunBearing {
  const gunner = flexibleBearing(gun, aim);
  if (gunner) return gunner;
  return { mount: gun.forwardMount, traverse: BODY_FORWARD_TRAVERSE, bears: gun.hasFixedGun };
}

/**
 * True when *some* station can be brought onto a target in body-frame direction
 * `aim` — a gunner inside his arc, or a fixed gun, which always counts.
 */
export function gunsBear(gun: GunSpec, aim: Vec3): boolean {
  return bearingFor(gun, aim).bears;
}

/**
 * **Every** station that can be brought onto the target, not just the first
 * one — because in the original a station is a *gun*, not a mode.
 *
 * `mg.c`'s firing entry takes a station index and works off two per-station
 * arrays: `planeParams+0x0a` is `.DAT` offset 216, the station count, and the
 * 16-byte records it bounds are the gun block (a Gotha's three, a Bristol's
 * two). `shooting.c` calls it once per *gunner entity*, each carrying its own
 * station index, its own rate accumulator (`aircraft[+0x6c] + station·4`) and
 * its own belt. So an aeroplane with two gunners who can both reach you is two
 * guns firing at once. See AD-011.
 *
 * **One divergence, and it is the port's ammunition model rather than a reading
 * of the original.** This port pools the whole aeroplane's belt into a single
 * `GunState`, so a station that fires spends the same rounds every other one
 * does. The fixed forward gun is therefore only fired when no gunner bears —
 * as it always was — because putting its rounds in the air *as well* would be
 * two streams drawn from one belt, and a two-seater with an enemy astern would
 * quietly get twice the volume of fire for the same ammunition. Where two
 * gunners' arcs genuinely overlap (the heavies' dorsal and ventral mounts) both
 * fire, which is the case the original's per-station model actually reaches.
 *
 * Ordered in `.DAT` station order with the fixed gun last, so a single-station
 * aeroplane produces exactly what `bearingFor` does.
 */
export function bearingsFor(gun: GunSpec, aim: Vec3): readonly GunBearing[] {
  const requested = traverseOf(aim);
  const bearings: GunBearing[] = [];
  for (const mount of gun.mounts) {
    if (!mount.flexible) continue;
    const aimed = aimMount(mount, requested);
    if (aimed.withinArc) bearings.push({ mount, traverse: aimed.traverse, bears: true });
  }
  if (bearings.length > 0) return bearings;
  // The fixed gun is aimed by flying the aeroplane, so it always bears; its
  // `.DAT` arc is a clamp on the round, not a statement about where it points.
  if (gun.hasFixedGun) {
    return [{ mount: gun.forwardMount, traverse: BODY_FORWARD_TRAVERSE, bears: true }];
  }
  return [];
}
